import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Eye, Clock, AlertCircle } from 'lucide-react';
import { dashboardService } from '../../../services/api';

export default function UpcomingFollowUpsWidget() {
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFollowUps();
  }, []);

  const loadFollowUps = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getUpcomingFollowUps();
      setFollowUps(response.data.data || []);
    } catch (error) {
      console.error('Error loading follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntil = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUpDate = new Date(date);
    followUpDate.setHours(0, 0, 0, 0);
    const diffTime = followUpDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFollowUpBadge = (date) => {
    const days = getDaysUntil(date);
    if (days < 0) return { 
      text: `${Math.abs(days)}d overdue`, 
      bg: 'bg-red-500/20', 
      textColor: 'text-red-400',
      icon: AlertCircle
    };
    if (days === 0) return { 
      text: 'Today', 
      bg: 'bg-orange-500/20', 
      textColor: 'text-orange-400',
      icon: Clock
    };
    if (days === 1) return { 
      text: 'Tomorrow', 
      bg: 'bg-yellow-500/20', 
      textColor: 'text-yellow-400',
      icon: Calendar
    };
    if (days <= 7) return { 
      text: `In ${days} days`, 
      bg: 'bg-amber-500/20', 
      textColor: 'text-amber-400',
      icon: Calendar
    };
    return { 
      text: `In ${days} days`, 
      bg: 'bg-green-500/20', 
      textColor: 'text-green-400',
      icon: Calendar
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Upcoming Follow-Ups</h3>
            <p className="text-sm text-slate-400">Scheduled contacts this week</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Sort by date (soonest first)
  const sortedFollowUps = [...followUps].sort((a, b) => {
    return new Date(a.next_followup_date) - new Date(b.next_followup_date);
  });

  // Get stats
  const overdueCount = followUps.filter(f => getDaysUntil(f.next_followup_date) < 0).length;
  const todayCount = followUps.filter(f => getDaysUntil(f.next_followup_date) === 0).length;
  const thisWeekCount = followUps.filter(f => {
    const days = getDaysUntil(f.next_followup_date);
    return days > 0 && days <= 7;
  }).length;

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Upcoming Follow-Ups</h3>
            <p className="text-sm text-slate-400">
              {sortedFollowUps.length} scheduled contact{sortedFollowUps.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {sortedFollowUps.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {overdueCount > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-center">
              <p className="text-red-400 font-bold text-lg">{overdueCount}</p>
              <p className="text-red-400 text-xs">Overdue</p>
            </div>
          )}
          {todayCount > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 text-center">
              <p className="text-orange-400 font-bold text-lg">{todayCount}</p>
              <p className="text-orange-400 text-xs">Today</p>
            </div>
          )}
          {thisWeekCount > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 text-center">
              <p className="text-amber-400 font-bold text-lg">{thisWeekCount}</p>
              <p className="text-amber-400 text-xs">This Week</p>
            </div>
          )}
        </div>
      )}

      {sortedFollowUps.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {sortedFollowUps.slice(0, 10).map((followUp, index) => {
            const badge = getFollowUpBadge(followUp.next_followup_date);
            const Icon = badge.icon;
            return (
              <div
                key={index}
                className="bg-slate-800 hover:bg-slate-700 rounded-lg p-3 transition-colors cursor-pointer group"
                onClick={() => navigate(`/admin/leads/view/${followUp.lead_id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`p-2 ${badge.bg} rounded-lg`}>
                      <Icon className={`w-4 h-4 ${badge.textColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{followUp.organisation_name}</p>
                      <p className="text-slate-400 text-xs">{followUp.consultant_name || 'Unassigned'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`font-bold text-sm ${badge.textColor}`}>
                        {formatDate(followUp.next_followup_date)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 ${badge.bg} ${badge.textColor} rounded-full font-semibold`}>
                        {badge.text}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/leads/view/${followUp.lead_id}`);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-2 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg transition-all"
                    >
                      <Eye className="w-4 h-4 text-amber-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">No upcoming follow-ups scheduled</p>
          <p className="text-slate-500 text-sm mt-1">Schedule follow-ups to stay on track</p>
        </div>
      )}

      {sortedFollowUps.length > 10 && (
        <div className="mt-4 pt-4 border-t border-slate-800">
          <p className="text-slate-400 text-sm text-center">
            Showing 10 of {sortedFollowUps.length} follow-ups
          </p>
        </div>
      )}
    </div>
  );
}